const template = `
	<s-popup v-model="shown" class="hourly-popup">
		<div slot="header">
			<template v-if="domain">{{ domain }} — </template>{{ ii(metric) }}
			<small v-if="loading" class="hourly-loading"> ({{ ii('LOADING') }}…)</small>
		</div>
		<div class="hourly-chart-wrap">
			<highcharts v-if="chartOpts" ref="chart" :options="chartOpts"></highcharts>
			<div v-else class="loading-label">{{ ii('LOADING') }}...</div>
			<div class="hourly-note">
				<span class="hourly-live">● live · 60s</span>
			</div>
		</div>
	</s-popup>
`;

// Reusable hourly compare popup: yesterday (grey) vs today (green) by hour of day.
// Opened imperatively from a parent: this.$refs.hourlyPopup.open(domain, metric).
// Honours the page's active filters (source, dates, …) via getExtendedQueryUrl.
export default {
	template,
	data() {
		return {
			shown: false,
			loading: false,
			domain: null,
			metric: null,
			yesterdayDate: '',
			todayDate: '',
			data: null,
			chartOpts: null,
			timer: null,
			ro: null
		};
	},
	methods: {
		open(domain, metric) {
			this.shown = true;
			this.loading = true;
			this.domain = domain || null;
			this.metric = metric;
			this.data = null;
			this.chartOpts = null;
			this.load();
			this.startPolling();
			document.addEventListener('keydown', this.onKeydown);
		},
		onKeydown(e) {
			if (e.key === 'Escape' || e.keyCode === 27) {
				this.shown = false;
			}
		},
		async load() {
			let scope = this.domain ? {domain: this.domain} : {};
			let base = this.getExtendedQueryUrl(scope).query;
			let opts = {...base, metric: this.metric};
			let res = await this.http.get('Stats.getHourlyCompare', opts);
			if (!this.shown || !res) return;
			this.yesterdayDate = res.yesterdayDate;
			this.todayDate = res.todayDate;
			this.data = res;
			this.loading = false;
			this.chartOpts = this.build(res);
			this.reflow();
		},
		build(res) {
			let cats = res.hours.map(h => (h < 10 ? '0' + h : '' + h) + ':00');
			return {
				chart: {
					type: 'column',
					height: 340,
					animation: false,
					spacingRight: 12,
					style: { fontFamily: 'Roboto, "Open Sans", sans-serif, helvetica' }
				},
				// title is already shown in the popup header
				title: { text: null },
				credits: { enabled: false },
				xAxis: {
					categories: cats,
					title: { text: null },
					tickmarkPlacement: 'on',
					labels: { step: 2, style: { fontSize: '10px' } },
					plotLines: [{
						value: res.currentHour,
						color: '#10b981',
						width: 1,
						dashStyle: 'Dash',
						zIndex: 5
					}]
				},
				yAxis: {
					min: 0,
					title: { text: null },
					allowDecimals: false
				},
				tooltip: { shared: true },
				legend: { enabled: true, itemStyle: { fontSize: '11px' } },
				plotOptions: {
					column: {
						// grouped: yesterday vs today side by side per hour
						borderWidth: 0,
						pointPadding: 0.03,
						groupPadding: 0.12,
						maxPointWidth: 18
					}
				},
				series: [
					{ name: 'Yesterday · ' + res.yesterdayDate, data: res.yesterday, color: '#94a3b8' },
					{ name: 'Today · ' + res.todayDate, data: res.today, color: '#10b981' }
				]
			};
		},
		reflow() {
			// the popup opens/animates to full width after the chart is created, so the
			// chart first renders into a too-narrow box — reflow to the real width.
			let doReflow = () => {
				let comp = this.$refs.chart;
				if (comp && comp.chart) {
					comp.chart.reflow();
				} else {
					window.dispatchEvent(new Event('resize'));
				}
			};
			this.$nextTick(() => {
				[0, 150, 350, 650, 1000].forEach(ms => setTimeout(doReflow, ms));
				let wrap = this.$el && this.$el.querySelector('.hourly-chart-wrap');
				if (wrap && window.ResizeObserver) {
					if (this.ro) this.ro.disconnect();
					this.ro = new ResizeObserver(() => doReflow());
					this.ro.observe(wrap);
				}
			});
		},
		startPolling() {
			this.stopPolling();
			// keep it live — refresh today's bars every 60s while the popup is open
			this.timer = setInterval(() => {
				if (this.shown) {
					this.load();
				} else {
					this.stopPolling();
				}
			}, 60000);
		},
		stopPolling() {
			if (this.timer) {
				clearInterval(this.timer);
				this.timer = null;
			}
			if (this.ro) {
				this.ro.disconnect();
				this.ro = null;
			}
			document.removeEventListener('keydown', this.onKeydown);
		}
	},
	watch: {
		shown(v) {
			if (!v) this.stopPolling();
		}
	},
	beforeDestroy() {
		this.stopPolling();
	}
};
